import React, { useEffect, useState } from "react";
import {
  AbsoluteFill,
  continueRender,
  delayRender,
  Img,
  interpolate,
  Sequence,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { getAudioDurationInSeconds } from "@remotion/media-utils";
import * as XLSX from "xlsx";
import FirstFrame from "./components/first-frame/FirstFrame";
import SecondFrame from "./components/second-frame/SecondFrame";
import ThirdFrame from "./components/third-frame/ThirdFrame";
import { LanguageProvider, useLanguage } from "./locales/LanguageContext";
import "../../../global.css";

const VIDEO_BASE_URL = (
  process.env.REACT_APP_VIDEO_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  "http://localhost:3050"
).replace(/\/$/, "");

// Audio playlist for the second frame.
export const audioList = [
  {
    src: `${VIDEO_BASE_URL}/audio/202606260_tc_msg2.mp3`,
    message:
      "We propose the view of bullish EUR/GBP because New Zealand's inflation exceeded forecasts at 3.1% y/y, boosting July rate hike \nexpectations while Bank of Japan may maintain unchanged policy due to Iran conflict uncertainties.",
  },
  {
    src: `${VIDEO_BASE_URL}/audio/202606260_en_msg1B.mp3`,
    message:
      "The combination of potential New Zealand rate increases and Japan's cautious monetary stance creates favorable conditions, \nwith a break above 95 likely testing 97.1.",
  },
  {
    src: `${VIDEO_BASE_URL}/audio/202606260_en_msg1B.mp3`,
    message:
      "We propose turning bullish on EUR vs. GBP at 94.00 with a target level of 97.10. We would turn neutral if EUR/GBP pair falls to \n92.45.",
  },
];

const BackgroundImgSrc = staticFile("images/background_still.png");

// Fixed lengths for the intro and outro frames (in frames).
const FIRST_FRAME_START = -10;
const FIRST_FRAME_DURATION = 560;
const SECOND_FRAME_START = 540;
const THIRD_FRAME_DURATION = 2000;
const DEFAULT_SECOND_FRAME_DURATION = 2560;

const Header = ({ hideStartFrame, hideEndFrame }) => {
  const frame = useCurrentFrame();
  const { durationInFrames, fps } = useVideoConfig();
  const { t, lang } = useLanguage();

  const canHide =
    hideStartFrame !== undefined &&
    hideEndFrame !== undefined &&
    hideEndFrame > hideStartFrame &&
    hideStartFrame > 50;

  const opacity = interpolate(frame, [0, 50], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const translateY = interpolate(frame, [0, 50], [-72, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const headlineOpacity = canHide
    ? interpolate(frame, [0, 50, hideStartFrame, hideEndFrame], [0, 1, 1, 0], {
        extrapolateLeft: "clamp",
        extrapolateRight: "clamp",
      })
    : opacity;
  const backgroundOpacity = headlineOpacity;

  const segmentsOf = (text) =>
    lang === "en"
      ? text.replace(/\s+/g, " ").trim().split(" ")
      : Array.from(text.replace(/\s+/g, "").trim());

  return (
    <div
      style={{
        position: "relative",
        zIndex: 20,
        width: "100%",
        minHeight: "130px",
        height: "auto",
        display: "flex",
        color: "white",
        fontSize: "30px",
        fontWeight: "600",
        lineHeight: "1.35",
        paddingTop: "22px",
        paddingBottom: "14px",
        paddingLeft: "40px",
        paddingRight: "40px",
        textAlign: "center",
        alignItems: "center",
        justifyContent: "center",
        opacity,
        transform: `translateY(${translateY}px)`,
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "#0c55a9",
          opacity: backgroundOpacity,
        }}
      />
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          height: "18px",
          backgroundColor: "#15e64c",
          width: `${(frame / durationInFrames) * 100}%`,
        }}
      />
      <p
        style={{
          margin: 0,
          padding: 0,
          position: "relative",
          opacity: headlineOpacity,
        }}
      >
        {segmentsOf(t.header).map((word, wordIndex, arr) => {
          const wordDelay = fps + wordIndex * 0.4;
          const wordOpacity = interpolate(frame - wordDelay, [0, 20], [0, 1], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
          });

          return (
            <span
              key={wordIndex}
              style={{
                display: "inline-block",
                opacity: wordOpacity,
                fontSize: lang === "en" ? "24px" : "36px",
                marginRight:
                  lang === "en" && wordIndex < arr.length - 1 ? "0.35rem" : "0",
              }}
            >
              {word}
            </span>
          );
        })}
      </p>
    </div>
  );
};

export const EnglishAdvertisement = ({
  lang = "en",
  date,
  color,
  backgroundColor,
  targetLevel = 1.354,
  bullishNumber = 1.301,
  turnNeutral = 1.375,
  audioList: audioListProp,
  icons,
  icon1,
  icon2,
  icon3,
  firstUnit,
  secondUnit,
  ccy1,
  ccy2,
  cnview,
}) => {
  const { fps, durationInFrames } = useVideoConfig();

  const resolvedAudioList =
    audioListProp !== undefined ? audioListProp : audioList;
  const audioKey = resolvedAudioList.map((audio) => audio?.src || "").join("|");

  const [apiDate, setApiDate] = useState(date || null);

  useEffect(() => {
    if (date) {
      setApiDate(date);
      return;
    }
    let isMounted = true;
    const fetchLatestApiDate = async () => {
      try {
        const res = await fetch(`${VIDEO_BASE_URL}/api/views`);
        if (res.ok) {
          const json = await res.json();
          if (json && Array.isArray(json.data) && json.data.length > 0) {
            const sorted = json.data
              .filter((item) => item?.date)
              .sort((a, b) => (a.date > b.date ? -1 : 1));
            if (sorted.length > 0 && sorted[0].date && isMounted) {
              setApiDate(sorted[0].date);
            }
          }
        }
      } catch (e) {
        // Fallback silently if API is unreachable
      }
    };
    fetchLatestApiDate();
    return () => {
      isMounted = false;
    };
  }, [date]);

  const [audioFrameDurations, setAudioFrameDurations] = useState(null);

  useEffect(() => {
    if (!resolvedAudioList || resolvedAudioList.length === 0) {
      setAudioFrameDurations([]);
      return;
    }

    const handle = delayRender("Measuring audio durations for second frame");
    let cancelled = false;

    (async () => {
      try {
        const seconds = await Promise.all(
          resolvedAudioList.map(async (audio) => {
            try {
              if (!audio || !audio.src) return 14.2;
              return await getAudioDurationInSeconds(audio.src);
            } catch (e) {
              return 14.2;
            }
          }),
        );
        if (!cancelled) {
          setAudioFrameDurations(seconds.map((s) => Math.ceil(s * fps)));
        }
      } catch (error) {
        console.warn(
          "Using default audio frame durations:",
          error?.message || error,
        );
      } finally {
        continueRender(handle);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [audioKey, fps]);

  const secondFrameDuration =
    audioFrameDurations && audioFrameDurations.length > 0
      ? audioFrameDurations.reduce((total, d) => total + d, 0)
      : DEFAULT_SECOND_FRAME_DURATION;

  const thirdFrameStart = SECOND_FRAME_START + secondFrameDuration - 5;
  const thirdFrameDuration = Math.max(
    THIRD_FRAME_DURATION,
    (durationInFrames || 3980) - thirdFrameStart,
  );

  const timingConfig = {
    firstFrameStart: FIRST_FRAME_START,
    firstFrameDuration: FIRST_FRAME_DURATION,
    secondFrameStart: SECOND_FRAME_START,
    secondFrameDuration,
    thirdFrameStart,
    thirdFrameDuration,
  };

  useEffect(() => {
    const loadExcelFile = async () => {
      try {
        const response = await fetch(
          staticFile("SCB CN High Conviction Idea Example File.xlsx"),
        );
        if (!response.ok) return;
        const arrayBuffer = await response.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: "array" });
        const sheetNames = workbook.SheetNames;
        console.log("Excel file worksheets:", sheetNames);

        sheetNames.forEach((sheetName) => {
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          console.log(`Worksheet "${sheetName}" data:`, jsonData);
          const jsonDataWithHeaders = XLSX.utils.sheet_to_json(worksheet);
          console.log(
            `Worksheet "${sheetName}" with headers:`,
            jsonDataWithHeaders,
          );
        });
      } catch (error) {
        console.warn("Skipping Excel file read:", error?.message || error);
      }
    };

    loadExcelFile();
  }, []);

  const resolvedFirstUnit = firstUnit || ccy1 || "";
  const resolvedSecondUnit = secondUnit || ccy2 || "";

  const computedBg =
    backgroundColor ||
    color ||
    (lang === "tc-CN" ? "#020B43" : "linear-gradient(90deg, #010d73, #0c55a9)");

  return (
    <LanguageProvider
      lang={lang}
      date={date || apiDate}
      firstUnit={resolvedFirstUnit}
      secondUnit={resolvedSecondUnit}
      viewLabel={cnview}
    >
      <AbsoluteFill
        style={{
          background: computedBg,
          backgroundColor:
            backgroundColor || (lang === "tc-CN" ? "#020B43" : "#0c55a9"),
          color: "white",
          fontFamily: "'Noto Sans SC', sans-serif",
        }}
      >
        <Header
          hideStartFrame={timingConfig.thirdFrameStart + 520}
          hideEndFrame={timingConfig.thirdFrameStart + 560}
        />
        {lang !== "tc-CN" && (
          <Img
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              zIndex: 0,
            }}
            src={BackgroundImgSrc}
          />
        )}

        <Sequence
          durationInFrames={timingConfig.firstFrameDuration}
          from={timingConfig.firstFrameStart}
          style={{ position: "relative", transform: "translateY(-50px)" }}
        >
          <FirstFrame
            firstUnit={resolvedFirstUnit}
            secondUnit={resolvedSecondUnit}
            exitFrame={
              timingConfig.secondFrameStart - timingConfig.firstFrameStart
            }
          />
        </Sequence>
        <Sequence
          from={timingConfig.secondFrameStart}
          durationInFrames={timingConfig.secondFrameDuration}
        >
          <SecondFrame
            lang={lang}
            audioList={resolvedAudioList}
            audioFrameDurations={audioFrameDurations}
            secondFrameDuration={secondFrameDuration}
            targetLevel={targetLevel}
            bullishNumber={bullishNumber}
            turnNeutral={turnNeutral}
            icons={icons}
            icon1={icon1}
            icon2={icon2}
            icon3={icon3}
            firstUnit={resolvedFirstUnit}
            secondUnit={resolvedSecondUnit}
            cnview={cnview}
          />
        </Sequence>
        <Sequence
          from={timingConfig.thirdFrameStart}
          durationInFrames={timingConfig.thirdFrameDuration}
        >
          <ThirdFrame />
        </Sequence>
      </AbsoluteFill>
    </LanguageProvider>
  );
};
